import { useState, useEffect, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  CustomDateRangePicker,
  CustomSelect,
  SearchInput,
  SkeletonText,
  StatusBadge,
} from '@/components';
import { V2Table } from '@/common/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { useMobileScrollLock, useOutsideClick } from '@/common/hooks';
import { useFormatters } from '../hooks/useFormatters';

import { getBulkCampaigns, getCampaignCost } from '../api';
import {
  useWhatsAppConnection,
  WHATSAPP_REQUEST_TIMEOUT_MS,
} from '../hooks/useWhatsAppConnection';
import {
  ListRequestError,
  WhatsAppListPrerequisite,
} from '../components/WhatsAppListPrerequisite';
import inrIcon from '../assets/INR.png';
import messageIcon from '../assets/message.png';
import gstIcon from '../assets/GST.png';
import sendIcon from '../assets/Send.png';

const CAMPAIGN_STATUS_OPTIONS = [
  { value: 'All', label: 'All Statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'processing', label: 'In Progress' },
  { value: 'pending', label: 'Pending' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'paused', label: 'Paused' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'failed', label: 'Failed' },
];

const STATUS_FILTER_STYLES = {
  All: { backgroundColor: '#F0F0F0', color: '#6B6B6B' },
  completed: { backgroundColor: '#E3F5E1', color: '#3FA34D' },
  processing: { backgroundColor: '#FBEFD1', color: '#C98A1F' },
  pending: { backgroundColor: '#FBE4D3', color: '#E07A3F' },
  scheduled: { backgroundColor: '#E0EEFB', color: '#2F6FED' },
  paused: { backgroundColor: '#FBEFD1', color: '#C98A1F' },
  stopped: { backgroundColor: '#F0F0F0', color: '#6B6B6B' },
  failed: { backgroundColor: '#FBDCE0', color: '#E0435C' },
};

function StatusFilterDropdown({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useMobileScrollLock(isOpen);
  useOutsideClick(ref, () => setIsOpen(false), isOpen);

  const selected = CAMPAIGN_STATUS_OPTIONS.find((o) => o.value === value);

  return (
    <div className="relative w-full sm:w-[170px]" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className="h-11 laptop:h-9 px-4 laptop:px-3 w-full rounded-[12px] border border-primary bg-white flex items-center justify-between gap-2 text-sm font-medium text-text-primary whitespace-nowrap focus:outline-none transition-colors"
      >
        <span>{selected?.label}</span>
        <svg
          className={`w-4 h-4 text-primary shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 8.25l-7.5 7.5-7.5-7.5"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-[calc(100%+8px)] z-30 w-full bg-white rounded-2xl shadow-2xl p-2 flex flex-col gap-2">
          {CAMPAIGN_STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              style={STATUS_FILTER_STYLES[opt.value]}
              className="w-full text-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-opacity hover:opacity-90"
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const CAMPAIGN_STATUS = {
  completed: { tone: 'success', label: 'Completed' },
  processing: { tone: 'warning', label: 'In Progress' },
  pending: { tone: 'info', label: 'Pending' },
  scheduled: { tone: 'info', label: 'Scheduled' },
  interval: { tone: 'warning', label: 'Interval' },
  paused: { tone: 'warning', label: 'Paused' },
  stopped: { tone: 'neutral', label: 'Stopped' },
  failed: { tone: 'error', label: 'Failed' },
};

const encodeId = (id) => {
  if (!id) return '';
  try {
    return btoa(String(id));
  } catch {
    return String(id);
  }
};

import { PaperAirplaneIcon } from '../components/Icons';
import { DashboardPage, DashboardFilterBar } from '../components/PremiumDesign';
const COST_FILTER_OPTIONS = [
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_15_days', label: 'Last 15 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
];

const CATEGORY_META = {
  marketing: {
    label: 'Marketing',
    color: '#2E7D32',
    bg: '#E8F5E9',
    border: '#C8E6C9',
  },
  utility: {
    label: 'Utility',
    color: '#E65100',
    bg: '#FFF3E0',
    border: '#FFE0B2',
  },
  authentication: {
    label: 'Authentication',
    color: '#303F9F',
    bg: '#E8EAF6',
    border: '#C5CAE9',
  },
  service: {
    label: 'Service',
    color: '#0288D1',
    bg: '#E1F5FE',
    border: '#B3E5FC',
  },
};

function CostBreakdownPopover({
  breakdown,
  currency = '₹',
  isOpen,
  onClose,
  triggerRef,
  selectedCategory,
  onSelectCategory,
}) {
  const popoverRef = useRef(null);

  useMobileScrollLock(isOpen);

  useOutsideClick([popoverRef, triggerRef], onClose, isOpen);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const categories = breakdown ? Object.entries(breakdown) : [];

  return (
    <div
      ref={popoverRef}
      data-overlay-container="true"
      style={{ overscrollBehavior: 'contain' }}
      onClick={(e) => e.stopPropagation()}
      className="absolute left-0 right-0 sm:left-auto sm:right-0 top-[calc(100%+8px)] z-50 w-full sm:w-[340px] max-w-[calc(100vw-24px)] bg-white rounded-2xl shadow-2xl border border-[#EBF4EA] p-3 sm:p-4 flex flex-col gap-3 animate-in fade-in zoom-in-95 duration-150"
    >
      <div className="flex items-center justify-between pb-2 border-b border-[#F0F0F0] gap-2">
        <div className="min-w-0">
          <h4 className="text-sm font-bold text-[#1F2241] truncate">
            Cost by Category
          </h4>
          <p className="text-[11px] text-[#868686] truncate">
            Per message rates including 18% GST
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {selectedCategory && (
            <button
              type="button"
              onClick={() => onSelectCategory(null)}
              className="text-[11px] font-semibold text-[#50AB3D] hover:underline px-1.5 py-0.5 rounded hover:bg-[#EBF4EA] transition-colors whitespace-nowrap"
            >
              Reset
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-6 h-6 rounded-full flex items-center justify-center text-[#868686] hover:text-[#1F2241] hover:bg-[#F5F5F5] transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {categories.length === 0 ? (
        <p className="text-xs text-center text-[#868686] py-3">
          No category breakdown available for this period.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {categories.map(([key, data]) => {
            const meta = CATEGORY_META[key.toLowerCase()] || {
              label: key.charAt(0).toUpperCase() + key.slice(1),
              color: '#4B5563',
              bg: '#F3F4F6',
              border: '#E5E7EB',
            };
            const isSelected =
              selectedCategory?.toLowerCase() === key.toLowerCase();
            const count = data?.count ?? 0;
            const rate =
              data?.rate != null ? Number(data.rate).toFixed(4) : '—';
            const cost =
              data?.cost != null ? Number(data.cost).toFixed(4) : '—';

            return (
              <button
                type="button"
                key={key}
                onClick={() => onSelectCategory(isSelected ? null : key)}
                className={`w-full text-left flex items-center justify-between p-2.5 rounded-xl border transition-all cursor-pointer hover:shadow-sm ${
                  isSelected
                    ? 'ring-2 ring-[#50AB3D] border-[#50AB3D] shadow-sm'
                    : 'hover:border-[#50AB3D]/50'
                }`}
                style={{
                  backgroundColor: meta.bg,
                  borderColor: isSelected ? '#50AB3D' : meta.border,
                }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {isSelected && (
                    <span className="w-4 h-4 rounded-full bg-[#50AB3D] text-white flex items-center justify-center shrink-0">
                      <svg
                        className="w-2.5 h-2.5 stroke-[3]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                  )}
                  <div className="flex flex-col min-w-0">
                    <span
                      className="text-xs font-bold uppercase tracking-wider truncate"
                      style={{ color: meta.color }}
                    >
                      {meta.label}
                    </span>
                    <span className="text-[11px] text-[#666666] mt-0.5 font-medium truncate">
                      {count} {count === 1 ? 'message' : 'messages'}
                    </span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end shrink-0 pl-2">
                  <span className="text-xs font-bold text-[#1F2241] whitespace-nowrap">
                    {rate !== '—' ? `${currency} ${rate}` : '—'}
                    <span className="text-[10px] font-normal text-[#868686]">
                      {' '}
                      / msg
                    </span>
                  </span>
                  <span className="text-[11px] text-[#666666] font-medium whitespace-nowrap">
                    Total: {cost !== '—' ? `${currency} ${cost}` : '—'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BillingMetricTile({
  iconSrc,
  iconColor,
  label,
  value,
  isLoading,
  breakdown,
  currency = '₹',
  selectedCategory,
  onSelectCategory,
}) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const tileRef = useRef(null);

  const hasBreakdown = breakdown && Object.keys(breakdown).length > 0;

  return (
    <div
      ref={tileRef}
      className={`relative flex items-center gap-2.5 sm:gap-3 laptop:!gap-2 bg-white rounded-xl border border-border-default px-3 sm:px-3.5 laptop:!px-3 py-2.5 sm:py-3 laptop:!py-2 min-w-0 ${
        hasBreakdown
          ? 'cursor-pointer hover:border-[#50AB3D]/60 transition-colors'
          : ''
      }`}
      onClick={() => {
        if (hasBreakdown) setIsPopoverOpen((prev) => !prev);
      }}
    >
      <span
        className="w-9 h-9 sm:w-10 sm:h-10 laptop:!w-8 laptop:!h-8 rounded-full flex items-center justify-center shrink-0 border-2"
        style={{ borderColor: iconColor }}
      >
        <span
          className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${iconColor}1A` }}
        >
          <img
            src={iconSrc}
            alt=""
            className="w-3.5 h-3.5 sm:w-4 sm:h-4 object-contain"
          />
        </span>
      </span>
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span
          className="text-[12px] sm:text-[13px] leading-tight text-text-secondary truncate"
          title={label}
        >
          {label}
        </span>
        {isLoading ? (
          <span className="inline-block w-20 h-5 bg-layer-5-card rounded animate-pulse" />
        ) : (
          <span
            className="text-[17px] sm:text-[18px] font-bold text-text-primary leading-tight truncate"
            title={value}
          >
            {value}
          </span>
        )}
      </div>

      {hasBreakdown && (
        <div className="shrink-0 flex items-center justify-center ml-0.5">
          <svg
            className={`w-3.5 h-3.5 text-[#50AB3D] transition-transform duration-200 ${
              isPopoverOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 8.25l-7.5 7.5-7.5-7.5"
            />
          </svg>
        </div>
      )}

      {hasBreakdown && (
        <CostBreakdownPopover
          breakdown={breakdown}
          currency={currency}
          isOpen={isPopoverOpen}
          onClose={() => setIsPopoverOpen(false)}
          triggerRef={tileRef}
          selectedCategory={selectedCategory}
          onSelectCategory={onSelectCategory}
        />
      )}
    </div>
  );
}

function CampaignBillingCard({
  costFilter,
  setCostFilter,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  costData,
  isCostLoading,
}) {
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Reset category selection on period or data change
  useEffect(() => {
    setSelectedCategory(null);
  }, [costFilter, costData]);

  const isCustomActive = costFilter === 'custom';

  const currencySymbol =
    costData?.currency === 'INR' ? '₹' : costData?.currency || '₹';
  const currencyCode = costData?.currency || 'INR';

  // Find active category data (case-insensitive lookup in breakdown)
  const activeCategoryData =
    selectedCategory && costData?.breakdown
      ? Object.entries(costData.breakdown).find(
          ([key]) => key.toLowerCase() === selectedCategory.toLowerCase(),
        )?.[1]
      : null;

  const selectedCategoryMeta = selectedCategory
    ? CATEGORY_META[selectedCategory.toLowerCase()]
    : null;
  const categoryLabel =
    selectedCategoryMeta?.label ||
    (selectedCategory
      ? selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)
      : '');

  // 1. Estimated Cost (shows total category cost when selected)
  const costAmount =
    selectedCategory && activeCategoryData?.cost != null
      ? `${currencyCode} ${Math.abs(parseFloat(activeCategoryData.cost || 0)).toFixed(2)}`
      : costData
        ? `${currencyCode} ${Math.abs(parseFloat(costData.total_messaging_cost || 0)).toFixed(2)}`
        : '—';

  const costLabel = selectedCategory
    ? `${categoryLabel} Cost`
    : 'Estimated Cost';

  // 2. Message Count (shows category message count when selected)
  const msgCount =
    selectedCategory && activeCategoryData?.count != null
      ? activeCategoryData.count
      : (costData?.total_successful_messages ?? '—');

  const msgLabel = selectedCategory ? `${categoryLabel} Messages` : 'Message';

  // 3. Per Message Rate (shows category rate when selected)
  const costPerMsg =
    selectedCategory && activeCategoryData?.rate != null
      ? parseFloat(activeCategoryData.rate).toFixed(4)
      : costData?.cost_per_message != null
        ? parseFloat(costData.cost_per_message).toFixed(4)
        : '—';

  const rateLabel = selectedCategory
    ? `${categoryLabel} including 18 % GST`
    : 'Per Message including 18 % GST';

  const messagingLimit = costData?.messaging_limit;
  const currentTierObj = messagingLimit?.tiers?.find((t) => t.is_current);
  const limitValue = messagingLimit
    ? messagingLimit.is_unlimited
      ? 'Unlimited'
      : currentTierObj?.label
        ? Number(currentTierObj.label)
          ? Number(currentTierObj.label).toLocaleString()
          : currentTierObj.label
        : messagingLimit.current_limit != null
          ? Number(messagingLimit.current_limit).toLocaleString()
          : messagingLimit.tier || '—'
    : '—';

  return (
    <div className="bg-[#F5F5F5] rounded-[12px] p-3 sm:p-3.5 laptop:p-3 shrink-0 flex flex-col gap-2.5 sm:gap-3 laptop:gap-2">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3">
        <div>
          <h2 className="text-[15px] sm:text-[16px] laptop:text-[16px] laptop:leading-5 font-semibold text-text-primary">
            Campaign Billing
          </h2>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 w-full sm:w-auto">
          <CustomSelect
            options={COST_FILTER_OPTIONS}
            value={isCustomActive ? '' : costFilter}
            onChange={(val) => {
              setCostFilter(val);
              setCustomStartDate('');
              setCustomEndDate('');
            }}
            placeholder="Filter Period"
            className="w-full sm:w-40 !h-[38px] laptop:!h-9"
            buttonClassName="!h-[38px] laptop:!h-9 border border-border-default bg-white rounded-xl w-full flex items-center justify-between px-3 text-xs sm:text-sm font-medium text-text-primary focus:outline-none transition-all hover:border-border-active"
            textSizeClass="text-xs sm:text-sm font-medium"
          />
          <CustomDateRangePicker
            startDate={customStartDate}
            endDate={customEndDate}
            className="w-full sm:w-auto"
            triggerClassName="w-full justify-between sm:w-auto !h-[38px] laptop:!h-9 !px-3 text-xs sm:text-sm"
            onApply={(start, end) => {
              setCustomStartDate(start);
              setCustomEndDate(end);
              setCostFilter('custom');
            }}
            onClear={() => {
              setCustomStartDate('');
              setCustomEndDate('');
              setCostFilter('last_7_days');
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
        <BillingMetricTile
          iconSrc={inrIcon}
          iconColor="#50AB3D"
          label={costLabel}
          value={costAmount}
          isLoading={isCostLoading}
        />
        <BillingMetricTile
          iconSrc={messageIcon}
          iconColor="#EEB90B"
          label={msgLabel}
          value={msgCount}
          isLoading={isCostLoading}
        />
        <BillingMetricTile
          iconSrc={gstIcon}
          iconColor="#0088FF"
          label={rateLabel}
          value={costPerMsg}
          isLoading={isCostLoading}
          breakdown={costData?.breakdown}
          currency={currencySymbol}
          selectedCategory={selectedCategory}
          onSelectCategory={(cat) => setSelectedCategory(cat)}
        />
        {(isCostLoading || messagingLimit != null) && (
          <div className="relative flex items-center gap-2.5 sm:gap-3 laptop:!gap-2 bg-white rounded-xl border border-border-default px-3 sm:px-3.5 laptop:!px-3 py-2.5 sm:py-3 laptop:!py-2 min-w-0">
            <span
              className="w-9 h-9 sm:w-10 sm:h-10 laptop:!w-8 laptop:!h-8 rounded-full flex items-center justify-center shrink-0 border-2"
              style={{ borderColor: '#50AB3D' }}
            >
              <span
                className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#50AB3D1A' }}
              >
                <img
                  src={sendIcon}
                  alt=""
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 object-contain"
                />
              </span>
            </span>
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <span
                className="text-[12px] sm:text-[13px] leading-tight text-text-secondary truncate"
                title="Messaging limit"
              >
                Messaging limit
              </span>
              {isCostLoading ? (
                <span className="inline-block w-20 h-5 bg-layer-5-card rounded animate-pulse" />
              ) : (
                <div className="flex items-baseline gap-1 min-w-0">
                  <span
                    className="text-[17px] sm:text-[18px] font-bold text-text-primary leading-tight truncate"
                    title={limitValue}
                  >
                    {limitValue}
                  </span>
                  <span className="text-[11px] sm:text-[12px] font-normal text-text-secondary shrink-0">
                    / 24h
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function CampaignListPage() {
  const { formatDate, formatNumber } = useFormatters();

  const navigate = useNavigate();

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();
  const whatsapp = useWhatsAppConnection(companyId);
  const whatsappReady = whatsapp.phase === 'connected';

  // Campaign List state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Query: Fetch campaigns
  const {
    data: campaignData,
    isLoading: isCampaignsInitialLoading,
    isFetching: isCampaignsFetching,
    isError: isCampaignsError,
    error: campaignsError,
    refetch: refetchCampaigns,
  } = useQuery({
    queryKey: [
      'bulk-campaign-campaigns',
      companyId,
      debouncedSearch,
      statusFilter,
      currentPage,
    ],
    enabled: whatsappReady,
    retry: false,
    queryFn: async ({ signal }) => {
      const params = { page: currentPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter !== 'All') {
        let statusVal = statusFilter.toLowerCase();
        if (statusVal === 'in progress') statusVal = 'processing';
        params.filter = `status=${statusVal}`;
      }
      return getBulkCampaigns(companyId, params, {
        signal,
        timeout: WHATSAPP_REQUEST_TIMEOUT_MS,
      });
    },
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isCampaignsFetching && campaignData !== undefined,
    300,
    500,
  );
  const isCampaignsLoadingSkeleton =
    isCampaignsInitialLoading || showDelayedSkeleton;

  useEffect(() => {
    if (isCampaignsError) {
      toast.error(
        campaignsError?.response?.data?.detail ||
          campaignsError?.response?.data?.code,
      );
    }
  }, [isCampaignsError, campaignsError]);

  const [costFilter, setCostFilter] = useState('last_7_days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const { data: costData, isLoading: isCostLoading } = useQuery({
    queryKey: [
      'campaign-cost',
      companyId,
      costFilter,
      customStartDate,
      customEndDate,
    ],
    queryFn: async ({ signal }) => {
      if (!companyId) return null;
      const params = { filter: costFilter };
      if (costFilter === 'custom') {
        if (!customStartDate || !customEndDate) return null;
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
      return getCampaignCost(companyId, params, {
        signal,
        timeout: WHATSAPP_REQUEST_TIMEOUT_MS,
      });
    },
    enabled:
      whatsappReady &&
      !!companyId &&
      (costFilter !== 'custom' || !!(customStartDate && customEndDate)),
    retry: false,
  });

  const isCostLoadingSkeleton = useDelayedSkeleton(isCostLoading);

  const campaignsList = campaignData?.results || [];
  const totalCampaignsCount = campaignData?.count || 0;
  const itemsPerPage = 10;

  // Format date helper
  const formatCampDate = (dateString) => {
    return formatDate(dateString, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const openCampaign = (camp) => navigate(`/campaigns/${encodeId(camp.id)}`);

  const countCell = (value, className) => (
    <span
      className={`truncate block tabular-nums text-[13px] sm:text-sm font-medium ${className}`}
    >
      {formatNumber(value)}
    </span>
  );

  const campaignColumns = [
    {
      id: 'name',
      label: 'Campaign Name',
      defaultWidth: 160,
      minWidth: 130,
      resizable: true,
      skeleton: <SkeletonText width="w-24" height="h-4" />,
      cell: (camp) => (
        <span
          className="truncate block text-[13px] sm:text-sm font-medium text-[#0F172A] whitespace-nowrap"
          title={`Campaign #${camp.id}`}
        >
          Campaign #{camp.id}
        </span>
      ),
    },
    {
      id: 'template',
      label: 'Template Name',
      defaultWidth: 180,
      minWidth: 140,
      resizable: true,
      skeleton: <SkeletonText width="w-32" height="h-4" />,
      cell: (camp) => (
        <span
          className="truncate block text-[13px] sm:text-sm text-[#1E293B] whitespace-nowrap"
          title={camp.template_name || ''}
        >
          {camp.template_name || '-'}
        </span>
      ),
    },
    {
      id: 'total',
      label: 'Total Contacts',
      defaultWidth: 130,
      minWidth: 110,
      resizable: true,
      align: 'center',
      skeleton: <SkeletonText width="w-6" height="h-4" className="mx-auto" />,
      cell: (camp) => countCell(camp.total_count, 'text-[#1F2241]'),
    },
    {
      id: 'sent',
      label: 'Sent',
      headerClassName: 'text-emerald-300',
      defaultWidth: 85,
      minWidth: 70,
      resizable: true,
      align: 'center',
      skeleton: <SkeletonText width="w-6" height="h-4" className="mx-auto" />,
      cell: (camp) => countCell(camp.sent_count, 'text-[#059669]'),
    },
    {
      id: 'failed',
      label: 'Failed',
      headerClassName: 'text-red-300',
      defaultWidth: 85,
      minWidth: 70,
      resizable: true,
      align: 'center',
      skeleton: <SkeletonText width="w-6" height="h-4" className="mx-auto" />,
      cell: (camp) => countCell(camp.failed_count, 'text-[#E02D3C]'),
    },
    {
      id: 'skipped',
      label: 'Skipped',
      headerClassName: 'text-yellow-300',
      defaultWidth: 95,
      minWidth: 80,
      resizable: true,
      align: 'center',
      skeleton: <SkeletonText width="w-6" height="h-4" className="mx-auto" />,
      cell: (camp) => countCell(camp.skipped_count, 'text-text-muted'),
    },
    {
      id: 'status',
      label: 'Status',
      defaultWidth: 130,
      minWidth: 110,
      resizable: true,
      align: 'center',
      skeleton: (
        <SkeletonText
          width="w-20"
          height="h-[26px]"
          className="mx-auto !rounded-full"
        />
      ),
      cell: (camp) => (
        <StatusBadge
          tone={CAMPAIGN_STATUS[camp.status]?.tone || 'neutral'}
          className="!h-[26px] laptop:!h-[22px] !px-2.5 laptop:!px-2 !text-xs font-semibold !gap-1"
        >
          {CAMPAIGN_STATUS[camp.status]?.label || camp.status}
        </StatusBadge>
      ),
    },
    {
      id: 'language',
      label: 'Language',
      defaultWidth: 100,
      minWidth: 80,
      resizable: true,
      skeleton: <SkeletonText width="w-12" height="h-4" />,
      cell: (camp) => (
        <span
          className="truncate block uppercase text-[13px] sm:text-sm text-[#475569] whitespace-nowrap"
          title={camp.template_language || ''}
        >
          {camp.template_language || '-'}
        </span>
      ),
    },
    {
      id: 'created',
      label: 'Created Date',
      defaultWidth: 170,
      minWidth: 140,
      resizable: true,
      skeleton: <SkeletonText width="w-32" height="h-4" />,
      cell: (camp) => (
        <span
          className="truncate block text-[13px] sm:text-sm text-text-muted whitespace-nowrap"
          title={formatCampDate(camp.created_at)}
        >
          {formatCampDate(camp.created_at)}
        </span>
      ),
    },
    {
      id: 'action',
      label: 'Action',
      defaultWidth: 140,
      minWidth: 120,
      resizable: false,
      align: 'center',
      stopPropagation: true,
      skeleton: (
        <SkeletonText
          width="w-[100px]"
          height="h-7"
          className="mx-auto rounded-lg"
        />
      ),
      cell: (camp) => (
        <button
          type="button"
          onClick={() => openCampaign(camp)}
          className="h-7 laptop:h-6 px-2.5 laptop:px-2 rounded-lg text-xs font-semibold text-primary hover:bg-primary/20 bg-primary/10 border border-primary/20 inline-flex items-center justify-center transition-all focus:outline-none whitespace-nowrap"
          title="View Campaign"
        >
          View Campaign
        </button>
      ),
    },
  ];

  return (
    <DashboardPage className="h-full flex-1 min-h-0 flex flex-col overflow-hidden !pt-2 sm:!pt-4 laptop:!pt-2">
      <div className="flex flex-col text-left w-full gap-3 sm:gap-4 laptop:gap-2.5 flex-1 min-h-0">
        <CampaignBillingCard
          costFilter={costFilter}
          setCostFilter={setCostFilter}
          customStartDate={customStartDate}
          setCustomStartDate={setCustomStartDate}
          customEndDate={customEndDate}
          setCustomEndDate={setCustomEndDate}
          costData={costData}
          isCostLoading={isCostLoadingSkeleton}
        />

        {/* Filter Bar */}
        <div className="shrink-0">
          <DashboardFilterBar className="!mb-0">
            <div className="flex flex-col sm:flex-row gap-3 laptop:gap-2 w-full sm:w-auto flex-grow max-w-lg">
              <SearchInput
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by Template Name or Template ID"
                className="flex-grow"
                inputClassName="laptop:!h-9"
              />

              {/* Status Select dropdown */}
              <StatusFilterDropdown
                value={statusFilter}
                onChange={(val) => {
                  setStatusFilter(val);
                  setCurrentPage(1);
                }}
              />
            </div>
          </DashboardFilterBar>
        </div>

        <div className="w-full min-w-0 flex-1 min-h-0 flex flex-col campaigns-table-compact [&_tbody_tr]:!h-[46px] [&_thead_tr]:!h-[42px] [&_td]:!py-2 [&_td]:!px-3 [&_th]:!py-2 [&_th]:!px-3 laptop:[&_tbody_tr]:!h-[44px] laptop:[&_thead_tr]:!h-[40px] laptop:[&_td]:!py-1 laptop:[&_th]:!py-1">
          <WhatsAppListPrerequisite connection={whatsapp}>
            {isCampaignsError ? (
              <ListRequestError
                message="Could not load campaigns. The request failed or timed out."
                onRetry={() => refetchCampaigns()}
              />
            ) : (
              <V2Table
                tight
                columns={campaignColumns}
                data={campaignsList}
                rowKey="id"
                storageKey="campaigns_table_column_widths"
                loading={whatsappReady && isCampaignsLoadingSkeleton}
                loadingRowCount={10}
                onRowClick={openCampaign}
                empty={
                  <div className="flex flex-col items-center justify-center gap-4">
                    <div className="w-16 h-16 bg-input-background border border-border-default rounded-full flex items-center justify-center">
                      <PaperAirplaneIcon className="w-8 h-8 opacity-45 text-text-secondary" />
                    </div>
                    <span className="text-base font-medium">
                      No campaigns found
                    </span>
                  </div>
                }
                pagination={{
                  currentPage,
                  totalCount: totalCampaignsCount,
                  itemsPerPage,
                  onChange: setCurrentPage,
                }}
              />
            )}
          </WhatsAppListPrerequisite>
        </div>
      </div>
    </DashboardPage>
  );
}
